import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { createRoot, Root } from 'react-dom/client';
import 'katex/dist/katex.min.css';
import { evaluateComponentModule } from '../componentSandbox';
import { hide, reveal } from '../DynamicComponentRenderer/domVisibility';

/**
 * Renders a premium slot component from its SERVER-COMPILED artifact.
 *
 * Identical sandbox to DynamicComponentRenderer — same SES Compartment, same
 * endowments, same customRequire and allowedModules — but it does no
 * transpiling, so it never loads @babel/standalone. That is the whole point:
 * the public page stops shipping readable JSX and stops paying for a
 * client-side Babel transform on every load.
 */
type PrecompiledComponentRendererProps = {
  componentCode: string;
  props: Record<string, unknown>;
  allowedModules?: string[];
  fallbackSelector?: string;
  placeholderSelector?: string;
};

const PrecompiledComponentRenderer = ({
  componentCode,
  props,
  allowedModules,
  fallbackSelector,
  placeholderSelector,
}: PrecompiledComponentRendererProps) => {
  const rootRef = useRef<Root | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fail = (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unknown dynamic component error.';
      console.error('Error rendering precompiled component:', error);
      hide(placeholderSelector);
      reveal(fallbackSelector);
      setErrorMessage(message);
    };

    try {
      const DynamicComponent = evaluateComponentModule(componentCode, allowedModules);

      if (!rootRef.current && containerRef.current) {
        rootRef.current = createRoot(containerRef.current);
      }

      if (rootRef.current) {
        const root = rootRef.current;
        // Commit the user component synchronously so it is in the DOM before we
        // hide the skeleton — avoids a skeleton→blank→content stutter.
        //
        // Deferred one microtask, and that is the load-bearing part. The old code
        // called flushSync directly in this effect under the comment "Safe here:
        // this runs in an effect (post-commit), not during render" -- which is true
        // of THIS component and false of the page. A challenge page mounts several
        // React roots via ReactRailsUJS, so this effect can fire while ANOTHER root
        // is mid-render; React then refuses the synchronous flush and warns
        // "flushSync was called from inside a lifecycle method ... React cannot
        // flush when React is already rendering". The flush is skipped, so the
        // skeleton→blank→content stutter this exists to prevent can still happen --
        // it just also logs.
        //
        // A microtask runs once the current synchronous work unwinds, i.e. after
        // React has finished flushing effects, so flushSync is no longer called
        // from inside a lifecycle. It still lands before paint, so the skeleton is
        // hidden in the same frame and nothing flickers.
        //
        // `cancelled` matters because the effect re-runs on prop changes: without
        // it, a microtask queued for the PREVIOUS componentCode could commit after
        // the new one, leaving the slot showing the older component.
        queueMicrotask(() => {
          if (cancelled) return;
          try {
            ReactDOM.flushSync(() => {
              root.render(React.createElement(DynamicComponent, props));
            });
            hide(placeholderSelector);
            setErrorMessage(null);
          } catch (error) {
            fail(error);
          }
        });
      }
    } catch (error) {
      fail(error);
    }

    return () => {
      cancelled = true;
    };
  }, [componentCode, props, allowedModules, fallbackSelector, placeholderSelector]);

  return (
    <div className="dynamic-component-renderer">
      <div ref={containerRef}></div>
      {errorMessage && (
        <div className="dynamic-component-renderer__error" data-dynamic-component-error="true" style={{ display: 'none' }}>
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default PrecompiledComponentRenderer;
